import React from 'react';
import { MdBusiness, MdCalendarToday, MdDescription, MdLabel } from 'react-icons/md';

import { getBatchStatusBadgeClass, getBatchStatusLabel } from './utils/statusLabels';

export interface BatchSummaryCounts {
    total: number;
    approved: number;
    rejected: number;
    pending: number;
    creditFlagged: number;
    errors: number;
    /** AF-3460: bureau respondeu, sem informação para o documento. */
    noData: number;
}

interface BatchInfoCardProps {
    provider?: string;
    identification?: string;
    status?: string;
    createdAt?: string;
    summary: BatchSummaryCounts;
}

const InfoItem: React.FC<{
    icon: React.ReactNode;
    label: string;
    children: React.ReactNode;
}> = ({ icon, label, children }) => (
    <div className="flex items-start gap-3">
        <span
            className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:bg-slate-700 dark:text-slate-300"
            aria-hidden="true"
        >
            {icon}
        </span>
        <div className="min-w-0">
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {label}
            </dt>
            <dd className="mt-0.5 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
                {children}
            </dd>
        </div>
    </div>
);

/** Contador do resumo. Só aparece quando há o que contar, para não poluir com zeros. */
const SummaryChip: React.FC<{
    label: string;
    value: number;
    className: string;
    alwaysShow?: boolean;
}> = ({ label, value, className, alwaysShow = false }) => {
    if (value === 0 && !alwaysShow) return null;
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${className}`}>
            <span className="font-bold">{value.toLocaleString('pt-BR')}</span>
            {label}
        </span>
    );
};

/**
 * AF-3418: substitui o bloco `bg-gray-50 p-4 rounded-lg` com grid de 4 colunas.
 *
 * Duas correções além do visual:
 *  - o Status era renderizado cru (`{batchData.status}`), então aparecia "Processing" para
 *    o usuário. Agora passa por getBatchStatusLabel e vira badge colorido.
 *  - o card não tinha nenhuma classe de dark mode, apesar de o app suportar.
 *
 * O resumo por recomendação é novo: antes o analista precisava contar na mão quantos
 * documentos estavam aprovados/reprovados no lote.
 */
const BatchInfoCard: React.FC<BatchInfoCardProps> = ({
    provider,
    identification,
    status,
    createdAt,
    summary,
}) => (
    <section
        className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800"
        aria-label="Informações do lote"
    >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Informações do Lote
            </h3>
            <span className={getBatchStatusBadgeClass(status)}>{getBatchStatusLabel(status)}</span>
        </div>

        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <InfoItem icon={<MdBusiness size={18} />} label="Provider">
                {provider || 'N/A'}
            </InfoItem>
            <InfoItem icon={<MdLabel size={18} />} label="Identificação">
                <span title={identification || undefined}>{identification || 'N/A'}</span>
            </InfoItem>
            <InfoItem icon={<MdDescription size={18} />} label="Documentos">
                {summary.total.toLocaleString('pt-BR')}
            </InfoItem>
            <InfoItem icon={<MdCalendarToday size={18} />} label="Processado em">
                {createdAt || 'N/A'}
            </InfoItem>
        </dl>

        <div className="mt-5 flex flex-wrap gap-2 border-t border-gray-100 pt-4 dark:border-slate-700">
            <SummaryChip
                label="aprovados"
                value={summary.approved}
                className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                alwaysShow
            />
            <SummaryChip
                label="reprovados"
                value={summary.rejected}
                className="bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                alwaysShow
            />
            <SummaryChip
                label="pendentes"
                value={summary.pending}
                className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200"
            />
            <SummaryChip
                label="sinalizados crédito"
                value={summary.creditFlagged}
                className="bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
            />
            <SummaryChip
                label="sem dados"
                value={summary.noData}
                className="border border-gray-300 bg-gray-100 text-gray-700 dark:border-slate-500 dark:bg-slate-700 dark:text-gray-200"
            />
            <SummaryChip
                label="com erro"
                value={summary.errors}
                className="border border-orange-400 bg-orange-100 text-orange-900 dark:border-orange-500 dark:bg-orange-900 dark:text-orange-100"
            />
        </div>
    </section>
);

export default BatchInfoCard;
